import React from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { migratePagePath } from 'src/data/pageRoutes';
import { customerCanMigrate } from 'src/modules/auth';
import { CommonContext } from 'src/modules/common/context';

import { MIGRATION_PAGE_ROUTES } from '../../data/constants';
import { useMigrationPreview } from '../../hooks/useMigrationPreview';
import MigrateLayout from '../../layout/MigrateLayout';
import { getMigrationGateRedirect } from '../../migrationGate';
import { getMigrationProvision } from '../../storage';
import AccountDetails from '../steps/AccountDetails';
import AccountMigration from '../steps/AccountMigration';
import ExportYourData from '../steps/ExportYourData';

const MIGRATION_STEPS = [
  {
    label: 'Account details',
    path: MIGRATION_PAGE_ROUTES.accountDetails
  },
  {
    label: 'Export your data',
    path: MIGRATION_PAGE_ROUTES.exportYourData
  },
  {
    label: 'Account migration',
    path: MIGRATION_PAGE_ROUTES.accountMigration
  }
];

const MigrateFlow: React.FC = () => {
  const navigate = useNavigate();
  const { step } = useParams();
  const { profile } = React.useContext(CommonContext);
  const { search } = useMigrationPreview();
  const customer = profile?.customer?.data;
  const canMigrate = customerCanMigrate(customer);
  const customerId = customer?.id || '';
  const provisionedMigration = React.useMemo(() => getMigrationProvision(customerId), [customerId]);

  const redirectTo = getMigrationGateRedirect({
    step,
    canMigrate,
    provision: provisionedMigration,
    search
  });

  const content = {
    [MIGRATION_PAGE_ROUTES.exportYourDataSlug]: <ExportYourData />,
    [MIGRATION_PAGE_ROUTES.accountDetailsSlug]: <AccountDetails />,
    [MIGRATION_PAGE_ROUTES.accountMigrationSlug]: <AccountMigration />
  }[step || ''];

  if (redirectTo) {
    return <Navigate to={`${redirectTo}${search}`} replace />;
  }

  if (!content) {
    return <Navigate to={`${MIGRATION_PAGE_ROUTES.accountDetails}${search}`} replace />;
  }

  return (
    <MigrateLayout
      title='Migrate to Paystack MFB'
      description='Move your business to Paystack MFB in a few steps.'
      handleClose={() => navigate(`${migratePagePath}${search}`)}
      steps={MIGRATION_STEPS.map(migrationStep => ({
        ...migrationStep,
        path: `${migrationStep.path}${search}`
      }))}
    >
      {content}
    </MigrateLayout>
  );
};

export default MigrateFlow;
